import type { DefaultValues, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import type { SearchData } from '@eti/providers';
import { useDateFormat, useSearch, useTranslation } from '@eti/providers';
import { TripType } from '@eti/schema-types';
import type { CabinClass, TimeOfDay } from '@eti/schema-types';
import type { MutableRefObject } from 'react';
import {
  NUMBER_OF_STOPS,
  TRIP_1_ARRIVAL_TIME,
  TRIP_1_DEPARTURE_TIME,
  TRIP_2_ARRIVAL_TIME,
  TRIP_2_DEPARTURE_TIME,
  TRIP_3_ARRIVAL_TIME,
  TRIP_3_DEPARTURE_TIME,
  TRIP_4_ARRIVAL_TIME,
  TRIP_4_DEPARTURE_TIME,
} from '../../constants/searchFiltersConstants';
import { type RebookingData } from '../../utils/rebooking';
import { getFormattedSearchData } from '../../utils/search';
import {
  CABIN_CLASS,
  CARRIER_CODES,
  DEPARTURE_DATE,
  DEPARTURE_TIME_OF_DAY,
  DESTINATION,
  MULTI_BOUND,
  ORIGIN,
  passengerFields,
  PASSENGERS_FORM_SECTION,
  RETURN_DATE,
  RETURN_TIME_OF_DAY,
  SINGLE_BOUND,
  TRIP_TYPE,
} from '../../widgets/search-form/constants/formFields';
import { formatGetDate } from '../../widgets/search-form/utils/format';
import {
  resolveSearchUrl,
  type SearchRedirectConfig,
  useSearchRedirectConfig,
} from '../../widgets/search-form/utils/searchRedirect';
import SearchForm from '../../widgets/search-form/components/SearchForm';
import { withSubSessionParam } from '../../subSession/withSubSessionParam';
import type { SearchLocationType } from './models';

export type FormBound = {
  origin?: SearchLocationType | null;
  destination?: SearchLocationType | null;
  departureDate: Date | string;
  returnDate: Date | string;
  departureTimeOfDay?: TimeOfDay | '';
  returnTimeOfDay?: TimeOfDay | '';
};

export type FormPassengers = {
  adults: number;
  children: number;
  childrenAges: number[];
  infants: number;
  infantsAges: number[];
};

export interface SearchFormValues {
  singleBound: FormBound;
  multiBound: FormBound[];
  cabinClass: CabinClass;
  directFlight: boolean;
  passengers: FormPassengers;
  tripType?: TripType;
  carrierCodes: string[];
}

type SearchFormContainerProps = {
  onSearch: (keepFilters: boolean, searchData: SearchData) => void;
  rebookingData?: RebookingData;
  search?: SearchData | null;
  searchFormWatchRef?: MutableRefObject<UseFormWatch<SearchFormValues> | null>;
  setValueRef?: MutableRefObject<UseFormSetValue<SearchFormValues> | null>;
  shouldShowRebookingBanner?: boolean;
};

export type TravelerTextsType = {
  adult: string;
  adults: string;
  child: string;
  children: string;
  infant: string;
  infants: string;
};

const handleOnSubmit =
  (
    onSearch: (keepFilters: boolean, searchData: SearchData) => void,
    travelerTexts: TravelerTextsType,
    searchRedirectConfig: SearchRedirectConfig | null,
    setSearchData: (searchData: SearchData) => void,
    currentSearchData?: SearchData,
    departureErrorMessage?: string,
    returnErrorMessage?: string,
  ) =>
  async (values: SearchFormValues) => {
    if (departureErrorMessage) {
      return;
    }
    if (returnErrorMessage) {
      return;
    }
    if (searchRedirectConfig) {
      const redirectUrl = await resolveSearchUrl(searchRedirectConfig, values);

      if (redirectUrl) {
        window.location.href = withSubSessionParam(redirectUrl, { isBackendUrl: true });
      }
    }

    const previousBoundCount = currentSearchData?.routes?.length;
    const hasBoundCountChanged =
      previousBoundCount !== undefined && previousBoundCount !== values.multiBound.length;

    const searchVariables = {
      ...getFormattedSearchData(values, travelerTexts),
      filters: {
        ...currentSearchData?.filters,
        ...(values.directFlight ? { [NUMBER_OF_STOPS]: '0' } : { [NUMBER_OF_STOPS]: null }),
        ...(values.tripType === TripType.OneWay ||
        values.tripType === TripType.Return ||
        hasBoundCountChanged
          ? {
              [TRIP_1_DEPARTURE_TIME]: null,
              [TRIP_1_ARRIVAL_TIME]: null,
              [TRIP_2_DEPARTURE_TIME]: null,
              [TRIP_2_ARRIVAL_TIME]: null,
              [TRIP_3_DEPARTURE_TIME]: null,
              [TRIP_3_ARRIVAL_TIME]: null,
              [TRIP_4_DEPARTURE_TIME]: null,
              [TRIP_4_ARRIVAL_TIME]: null,
            }
          : {}),
      },
    };

    if (setSearchData) {
      setSearchData(searchVariables);
    }

    sessionStorage.removeItem('hideTripReminder');
    sessionStorage.removeItem('selectedTripSelectionKey');
    onSearch(true, searchVariables);
  };

const SearchFormContainer = ({
  onSearch,
  rebookingData,
  search,
  searchFormWatchRef,
  setValueRef,
  shouldShowRebookingBanner,
}: SearchFormContainerProps) => {
  const { t } = useTranslation();
  const { currentSearchData, setSearchData } = useSearch();
  const searchRedirectConfig = useSearchRedirectConfig();
  const travelerTexts = {
    adult: t('Adult'),
    adults: t('Adults'),
    child: t('Child'),
    children: t('Children'),
    infant: t('Infant'),
    infants: t('Infants'),
  };
  const { date } = useDateFormat();
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const sevenDaysAfter = new Date(today);
  sevenDaysAfter.setDate(today.getDate() + 7);
  const defaultPassengerForm = {
    [passengerFields.ADULTS]: 1,
    [passengerFields.CHILDREN]: 0,
    [passengerFields.CHILDREN_AGES]: [7, 7, 7, 7, 7, 7, 7, 7, 7],
    [passengerFields.INFANTS]: 0,
    [passengerFields.INFANTS_AGES]: [1, 1, 1, 1, 1, 1, 1, 1, 1],
  };

  const getMethods = (): DefaultValues<SearchFormValues> => {
    if (rebookingData && search) {
      return {
        ...search,
        [PASSENGERS_FORM_SECTION]: {
          [passengerFields.ADULTS]: rebookingData.initialValues.passengers.adults,
          [passengerFields.CHILDREN]: rebookingData.initialValues.passengers.children,
          [passengerFields.CHILDREN_AGES]: rebookingData.initialValues.passengers.childrenAges,
          [passengerFields.INFANTS]: rebookingData.initialValues.passengers.infants,
          [passengerFields.INFANTS_AGES]: rebookingData.initialValues.passengers.infantsAges,
        },
      };
    }

    if (search) {
      return {
        ...search,
        [PASSENGERS_FORM_SECTION]: search[PASSENGERS_FORM_SECTION] ?? defaultPassengerForm,
      };
    }

    return {
      [SINGLE_BOUND]: {
        [ORIGIN]: null,
        [DESTINATION]: null,
        [DEPARTURE_DATE]: formatGetDate(date)(tomorrow),
        [RETURN_DATE]: formatGetDate(date)(sevenDaysAfter),
        [DEPARTURE_TIME_OF_DAY]: '',
        [RETURN_TIME_OF_DAY]: '',
      },
      [MULTI_BOUND]: [
        {
          [ORIGIN]: null,
          [DESTINATION]: null,
          [DEPARTURE_DATE]: formatGetDate(date)(tomorrow),
        },
        {
          [ORIGIN]: null,
          [DESTINATION]: null,
          [DEPARTURE_DATE]: '',
        },
      ],
      [CABIN_CLASS]: 'ECONOMY',
      [CARRIER_CODES]: [],
      [PASSENGERS_FORM_SECTION]: defaultPassengerForm,
      [TRIP_TYPE]: TripType.Return,
    };
  };

  const methods = useForm<SearchFormValues>({
    defaultValues: getMethods(),
    mode: 'onBlur',
  });

  if (setValueRef) {
    setValueRef.current = methods.setValue;
  }

  if (searchFormWatchRef) {
    searchFormWatchRef.current = methods.watch;
  }

  return (
    <FormProvider {...methods}>
      <form
        aria-label={t('ScreenReader.SearchForm.FormLabel')}
        onSubmit={methods.handleSubmit(
          handleOnSubmit(
            onSearch,
            travelerTexts,
            searchRedirectConfig,
            setSearchData,
            currentSearchData,
          ),
        )}
      >
        <SearchForm
          rebookingData={rebookingData}
          shouldShowRebookingBanner={shouldShowRebookingBanner}
        />
        <DevTool control={methods.control} />
      </form>
    </FormProvider>
  );
};

export default SearchFormContainer;
